import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, Alert, Modal, Image } from 'react-native';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals } from '@/constants/theme';
import { trackEvent } from '@/lib/analytics';
import { supabase } from '@/lib/supabase';

export default function SignUpScreen() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailBlurred, setEmailBlurred] = useState(false);
  const [passwordBlurred, setPasswordBlurred] = useState(false);
  const [confirmPasswordBlurred, setConfirmPasswordBlurred] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const router = useRouter();

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    router.replace('/(auth)/sign-in');
  };

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const emailError =
    emailBlurred && (email.trim() === '' || !isValidEmail(email))
      ? email.trim() === ''
        ? 'Email is required'
        : 'Please enter a valid email address'
      : undefined;

  const passwordRequirementError =
    passwordBlurred && (password.length < 8 || password.length > 64)
      ? password.length > 64
        ? 'Password must be at most 64 characters'
        : 'Password must be at least 8 characters'
      : undefined;

  const confirmPasswordError =
    confirmPasswordBlurred && password !== confirmPassword
      ? 'Passwords do not match'
      : undefined;

  const handleSignUp = async () => {
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8 || password.length > 64) {
      setError(password.length < 8 ? 'Password must be at least 8 characters' : 'Password must be at most 64 characters');
      return;
    }

    setError('');
    setLoading(true);

    try {
      if (!supabase) {
        Alert.alert('Error', 'Supabase is not configured. Please set environment variables.');
        setLoading(false);
        return;
      }

      // Set different redirect URLs for web vs mobile
      const isWeb = Platform.OS === 'web';
      const emailRedirectTo = isWeb && typeof window !== 'undefined' && window.location
        ? `${window.location.origin}/verify-email`
        : 'sagechef://verify-email';

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo,
          data: fullName.trim() ? {
            full_name: fullName.trim(),
            display_name: fullName.trim().split(' ')[0], // First name as display name
          } : {},
        },
      });

      if (signUpError) {
        let errorMessage = 'Unable to create your account. Please try again.';
        if (signUpError.message.includes('already registered') || signUpError.message.includes('already exists')) {
          errorMessage = 'An account with this email already exists. Try signing in instead.';
        } else if (signUpError.message.includes('Password should be at least')) {
          errorMessage = 'Password must be at least 8 characters long.';
        } else if (signUpError.message.includes('Invalid email')) {
          errorMessage = 'Please enter a valid email address.';
        }
        setError(errorMessage);
      } else if (data) {
        trackEvent('User signed up');
        setShowSuccessModal(true);
      }
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Sign up error:', err);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    isValidEmail(email) &&
    password &&
    confirmPassword &&
    password === confirmPassword &&
    password.length >= 8 &&
    password.length <= 64;

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.header}>
            <View style={styles.logoContainer}>
              <Image
                source={require('@/assets/images/icon.png')}
                style={styles.logo}
                resizeMode="contain"
                accessibilityLabel="Sage Chef logo"
              />
            </View>
            <Text style={styles.title}>Create Account</Text>
          </View>

          <View style={styles.card}>
            <View style={styles.cardHeader}>
              <Ionicons name="lock-closed" size={20} color={Brand.primary} />
              <Text style={styles.cardTitle}>Sign Up</Text>
            </View>

            {error ? (
              <View style={styles.errorContainer}>
                <Ionicons name="alert-circle" size={16} color={Brand.destructive} />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}

            <Input
              label="Full Name (Optional)"
              placeholder="John Doe"
              autoCapitalize="words"
              autoComplete="name"
              value={fullName}
              onChangeText={setFullName}
              style={styles.input}
            />

            <Input
              label="Email"
              placeholder="you@example.com"
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              value={email}
              onChangeText={setEmail}
              onBlur={() => setEmailBlurred(true)}
              error={emailError}
              style={styles.input}
            />

            <Input
              label="Password"
              placeholder="8-64 characters"
              secureTextEntry
              autoCapitalize="none"
              autoComplete="password-new"
              value={password}
              onChangeText={setPassword}
              onBlur={() => setPasswordBlurred(true)}
              error={passwordRequirementError}
              style={styles.input}
            />

            <Input
              label="Confirm Password"
              placeholder="Confirm your password"
              secureTextEntry
              autoCapitalize="none"
              autoComplete="password-new"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              onBlur={() => setConfirmPasswordBlurred(true)}
              error={confirmPasswordError}
              style={styles.input}
            />

            <Button
              onPress={handleSignUp}
              disabled={!isFormValid || loading}
              loading={loading}
              style={styles.submitButton}
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </Button>

            <View style={styles.footer}>
              <Text style={styles.footerText}>Already have an account?</Text>
              <Button
                variant="ghost"
                onPress={() => router.push('/(auth)/sign-in')}
                style={styles.linkButton}
              >
                Sign In
              </Button>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>

      {/* Sign Up Success Modal */}
      <Modal
        visible={showSuccessModal}
        transparent
        animationType="fade"
        onRequestClose={handleSuccessModalClose}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Account Created</Text>
            <Text style={styles.modalDescription}>
              Please check your email to verify your account. Then sign in to continue.
            </Text>
            <View style={styles.modalActions}>
              <Button onPress={handleSuccessModalClose} style={styles.modalButton}>
                OK
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    justifyContent: 'center',
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  header: {
    marginBottom: 32,
    alignItems: 'center',
  },
  logoContainer: {
    width: 100,
    height: 100,
    marginBottom: 20,
    borderRadius: 24,
    overflow: 'hidden',
  },
  logo: {
    width: '100%',
    height: '100%',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Brand.primary,
    textAlign: 'center',
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: 24,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 2,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Brand.destructiveLight,
    padding: 12,
    borderRadius: 12,
    marginBottom: 16,
  },
  errorText: {
    flex: 1,
    fontSize: 14,
    color: Brand.destructive,
  },
  input: {
    marginBottom: 16,
  },
  submitButton: {
    marginTop: 8,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 24,
    gap: 8,
  },
  footerText: {
    fontSize: 14,
    color: Neutrals.textSecondary,
  },
  linkButton: {
    paddingHorizontal: 0,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: Platform.OS === 'android' ? 0 : 10,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    flex: 1,
  },
});
